/**
 * タスク詳細サイドピークの「幅」の唯一の実装。
 *
 * サイドピーク / ポップアップ / フルページの 3 モードは入れ物が違うだけで中身は共通
 * (lib/task-detail-open-mode.ts 参照)。そのうち **サイドピークだけ**は幅をユーザーが
 * ドラッグで変えられる。決定・クランプはここに集約し、
 * hooks/use-task-detail-peek.ts と components/task-detail-peek-layout.tsx が共有する。
 * 3 ビュー (Notebooks / Today / Tag) はすべて TaskDetailPeekLayout を通るので、
 * ここを直せば全ビューに効く。ビューごとに幅の計算をコピペしないこと。
 *
 * ## 保存先がなぜ AppSettings なのか
 * 旧実装は localStorage (`donezo:task-detail-peek-width`) に置いていたが、
 * localStorage は **オリジンごとに別物** (`https://donezo...` と `tauri://localhost`) なので
 * Web で広げてもデスクトップアプリには一切引き継がれない。displayZoom と同じ理由で
 * `AppSettings.taskDetailPeekWidth` (`user_settings` の `app-settings` キー) に置く。
 *
 * ## 上限をビューポート比にしている理由
 * 旧実装の上限は 600px 固定で、広いディスプレイでも詳細を半分までしか開けなかった
 * (「もっと広げたい」の原因)。上限を画面幅に対する割合にすると、
 * ノートPC でも 4K でも「画面をはみ出さない範囲でめいっぱい」まで広げられる。
 */

/** 下限。これより狭いとサイドバー内のラベル + 入力が破綻する (旧実装と同値) */
export const TASK_DETAIL_PEEK_MIN_WIDTH = 280;

/**
 * 上限はビューポート幅の 90%。
 * 100% にしないのは、一覧側が完全に隠れると「どのタスクを見ているか」の
 * 手掛かりが消えるため (全画面が欲しいときは Cmd+Shift+O の全画面表示がある)。
 */
export const TASK_DETAIL_PEEK_MAX_WIDTH_RATIO = 0.9;

/** 未設定時の既定幅 = 420px (2026-09-25 Redesign v2。旧実装はビューポート幅の 30%)。 */
export const TASK_DETAIL_PEEK_AUTO_WIDTH = 420;

/**
 * 未設定を表す値。`AppSettings.taskDetailPeekWidth` の既定。
 * 0 のときは「ユーザーが一度も幅を決めていない」= 自動 (ビューポート幅の 30%) とする。
 * 固定値を既定にすると、既存ユーザーの見え方が画面サイズによって変わってしまう。
 */
export const DEFAULT_TASK_DETAIL_PEEK_WIDTH = 0;

/** SSR / jsdom で window.innerWidth が取れないときの仮のビューポート幅 */
export const TASK_DETAIL_PEEK_FALLBACK_VIEWPORT = 1280;

/** キーボード (←/→) でリサイズするときの刻み。Shift 併用で 4 倍動かす */
export const TASK_DETAIL_PEEK_KEYBOARD_STEP = 24;

/** 旧実装の localStorage キー。設定が未設定のときだけ引き継ぎに使う */
export const LEGACY_TASK_DETAIL_PEEK_WIDTH_KEY = "donezo:task-detail-peek-width";

function safeViewportWidth(viewportWidth: number): number {
  return Number.isFinite(viewportWidth) && viewportWidth > 0
    ? viewportWidth
    : TASK_DETAIL_PEEK_FALLBACK_VIEWPORT;
}

/**
 * そのビューポート幅で許す最大幅。
 * 画面が極端に狭い場合でも下限を下回らない (下限 > 上限 になると clamp が壊れる)。
 */
export function getTaskDetailPeekMaxWidth(viewportWidth: number): number {
  const viewport = safeViewportWidth(viewportWidth);
  return Math.max(
    TASK_DETAIL_PEEK_MIN_WIDTH,
    Math.floor(viewport * TASK_DETAIL_PEEK_MAX_WIDTH_RATIO)
  );
}

/** 下限・上限に収める。NaN 等は下限に丸める (幅 0 のパネルを作らない) */
export function clampTaskDetailPeekWidth(
  width: unknown,
  viewportWidth: number
): number {
  const parsed = typeof width === "string" ? Number.parseFloat(width) : width;
  const max = getTaskDetailPeekMaxWidth(viewportWidth);
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) {
    return TASK_DETAIL_PEEK_MIN_WIDTH;
  }
  return Math.round(
    Math.min(max, Math.max(TASK_DETAIL_PEEK_MIN_WIDTH, parsed))
  );
}

/**
 * 設定に保存する値へ正規化する。
 * 不正値・0 以下は「未設定 (自動)」に落とす。ビューポート依存のクランプはここではしない
 * (狭い画面で一度開いただけで、広い画面用に決めた幅が縮んで保存されるのを避ける)。
 */
export function normalizeTaskDetailPeekWidth(value: unknown): number {
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_TASK_DETAIL_PEEK_WIDTH;
  }
  return Math.round(parsed);
}

/** 未設定 (自動) のときの幅 = 420px をビューポートに収めた値 */
export function getAutoTaskDetailPeekWidth(viewportWidth: number): number {
  const viewport = safeViewportWidth(viewportWidth);
  return clampTaskDetailPeekWidth(TASK_DETAIL_PEEK_AUTO_WIDTH, viewport);
}

/**
 * 実際に描画に使う幅。
 * 設定が未設定なら自動幅、設定済みならその値をビューポートに収めて返す。
 */
export function resolveTaskDetailPeekWidth(
  setting: unknown,
  viewportWidth: number
): number {
  const normalized = normalizeTaskDetailPeekWidth(setting);
  if (normalized === DEFAULT_TASK_DETAIL_PEEK_WIDTH) {
    return getAutoTaskDetailPeekWidth(viewportWidth);
  }
  return clampTaskDetailPeekWidth(normalized, viewportWidth);
}

/** キーボード操作用。`direction` は +1 で広げる (= 左へ伸ばす)、-1 で狭める */
export function stepTaskDetailPeekWidth(
  current: number,
  direction: number,
  viewportWidth: number,
  step: number = TASK_DETAIL_PEEK_KEYBOARD_STEP
): number {
  return clampTaskDetailPeekWidth(
    current + step * Math.sign(direction),
    viewportWidth
  );
}

/** いまのビューポート幅 (SSR では仮の値) */
export function getViewportWidth(): number {
  if (typeof window === "undefined") return TASK_DETAIL_PEEK_FALLBACK_VIEWPORT;
  return safeViewportWidth(window.innerWidth);
}

/**
 * 旧 localStorage 実装からの引き継ぎ。
 * 設定 (AppSettings) が未設定のときだけ参照する読み取り専用の移行パス。
 * 書き戻しはしない (正は AppSettings)。
 */
export function readLegacyTaskDetailPeekWidth(): number {
  if (typeof localStorage === "undefined") {
    return DEFAULT_TASK_DETAIL_PEEK_WIDTH;
  }
  try {
    return normalizeTaskDetailPeekWidth(
      localStorage.getItem(LEGACY_TASK_DETAIL_PEEK_WIDTH_KEY)
    );
  } catch {
    return DEFAULT_TASK_DETAIL_PEEK_WIDTH;
  }
}
